import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { AppContext } from 'views/App/context';

export const getOrgRiskPoolStatus = async (
  orgId: string,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<boolean> => {
  try {
    const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/risk-pool`;
    const data = await fetcher({
      method: 'get',
      url,
      options: { signal },
    });

    // Handle both boolean and string responses from API
    const riskPoolValue = data?.riskPoolEnabled;
    const isEnabled = riskPoolValue === true || riskPoolValue === 'true';
    return isEnabled;
  } catch (error: unknown) {
    console.error('Risk Pool API Error:', error);
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export function useOrgRiskPoolStatus(
  orgId: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<boolean, ServerErrorResponse>(
    ['riskPoolStatus', orgId],
    async ({ signal }): Promise<boolean> => {
      return getOrgRiskPoolStatus(orgId, signal, onError);
    },
    {
      keepPreviousData: true,
      enabled: !!orgId,
      ...options,
    }
  );
}

export function useOrgRiskPoolMutations() {
  const queryClient = useQueryClient();
  const { invalidateRiskPoolCache } = useContext(AppContext);

  const enableOrgRiskPool = useMutation(
    async (orgId: string) => {
      const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/risk-pool`;
      return await fetcher({
        method: 'post',
        url,
        body: {},
      });
    },
    {
      onSuccess: (_, orgId) => {
        queryClient.invalidateQueries(['riskPoolStatus', orgId]);
        invalidateRiskPoolCache(orgId);
      },
    }
  );

  const disableOrgRiskPool = useMutation(
    async (orgId: string) => {
      const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/risk-pool`;
      return await fetcher({
        method: 'delete',
        url,
      });
    },
    {
      onSuccess: (_, orgId) => {
        queryClient.invalidateQueries(['riskPoolStatus', orgId]);
        invalidateRiskPoolCache(orgId);
      },
    }
  );

  return {
    enableOrgRiskPool,
    disableOrgRiskPool,
  };
}